export interface ApiEnvelope<T> {
  requestId: string;
  data?: T;
}

export interface ApiErrorDetail {
  field?: string;
  code?: string;
  message: string;
  value?: unknown;
}

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: ApiErrorDetail[];
}

export interface ApiErrorEnvelope {
  requestId: string;
  error?: ApiErrorBody;
}

export interface ParsedEnvelope<T> {
  requestId?: string;
  data?: T;
  error?: ApiErrorBody;
}

export class ApiError extends Error {
  readonly code: string;
  readonly requestId?: string;
  readonly details: ApiErrorDetail[];

  constructor(code: string, message: string, requestId?: string, details?: ApiErrorDetail[]) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.requestId = requestId;
    this.details = details ?? [];
  }
}

/**
 * Read a list field from SDK response bodies:
 * - `{ items: T[] }`, `{ data: { items: T[] } }`, or `{ data: T[] }` (legacy mocks).
 */
export function readListFromEnvelope<T>(envelope: unknown, keys: readonly string[]): T[] {
  if (envelope == null) return [];
  if (Array.isArray(envelope)) return envelope as T[];
  if (typeof envelope !== 'object') return [];

  const record = envelope as Record<string, unknown>;
  for (const key of keys) {
    const direct = record[key];
    if (Array.isArray(direct)) return direct as T[];
  }

  const data = record.data;
  if (Array.isArray(data)) return data as T[];
  if (data != null && typeof data === 'object') {
    const nested = data as Record<string, unknown>;
    for (const key of keys) {
      const nestedList = nested[key];
      if (Array.isArray(nestedList)) return nestedList as T[];
    }
  }

  return [];
}

/**
 * Safely read `envelope.data`. Never throws when React Query `data` is still undefined.
 * When `fallback` is omitted and data is missing, returns `undefined`.
 */
export function readDataFromEnvelope<T>(envelope: unknown): T | undefined;
export function readDataFromEnvelope<T>(envelope: unknown, fallback: T): T;
export function readDataFromEnvelope<T>(envelope: unknown, fallback?: T): T | undefined {
  if (envelope == null || typeof envelope !== 'object') return fallback;
  const data = (envelope as Record<string, unknown>).data;
  if (data === undefined) return fallback;
  return data as T;
}

/**
 * Read a named field from the envelope root, then from `envelope.data`.
 * Use for shapes like `{ session }` / `{ data: { session } }` / `{ settings }`.
 * Never throws on nullish envelopes.
 */
export function readObjectFieldFromEnvelope<T>(
  envelope: unknown,
  keys: readonly string[],
): T | undefined;
export function readObjectFieldFromEnvelope<T>(
  envelope: unknown,
  keys: readonly string[],
  fallback: T,
): T;
export function readObjectFieldFromEnvelope<T>(
  envelope: unknown,
  keys: readonly string[],
  fallback?: T,
): T | undefined {
  if (envelope == null || typeof envelope !== 'object') return fallback;
  const record = envelope as Record<string, unknown>;

  for (const key of keys) {
    const direct = record[key];
    if (direct !== undefined && direct !== null) return direct as T;
  }

  const data = record.data;
  if (data != null && typeof data === 'object') {
    const nested = data as Record<string, unknown>;
    for (const key of keys) {
      const nestedValue = nested[key];
      if (nestedValue !== undefined && nestedValue !== null) return nestedValue as T;
    }
  }

  return fallback;
}

/**
 * Prefer `envelope.data`, then alternate top-level keys (`order`, `summary`, …).
 * Covers the common `e.data ?? e.order` pattern without throwing.
 */
export function readEntityFromEnvelope<T>(
  envelope: unknown,
  altKeys: readonly string[] = [],
): T | undefined {
  if (envelope == null || typeof envelope !== 'object') return undefined;
  const record = envelope as Record<string, unknown>;
  if (record.data !== undefined && record.data !== null) return record.data as T;
  for (const key of altKeys) {
    const value = record[key];
    if (value !== undefined && value !== null) return value as T;
  }
  return undefined;
}

export function parseEnvelope<T>(body: unknown): ParsedEnvelope<T> {
  if (!body || typeof body !== 'object') {
    return {
      error: { code: 'ERR-UNKNOWN', message: 'Invalid response' },
    };
  }

  const envelope = body as ApiEnvelope<T> & ApiErrorEnvelope;

  if (envelope.error) {
    return {
      requestId: envelope.requestId,
      error: envelope.error,
    };
  }

  return {
    requestId: envelope.requestId,
    data: envelope.data,
  };
}

/** Throws `ApiError` when the envelope contains an error or missing data. */
export function unwrapEnvelope<T>(body: unknown): { requestId?: string; data: T } {
  const parsed = parseEnvelope<T>(body);
  if (parsed.error) {
    throw new ApiError(
      parsed.error.code,
      parsed.error.message,
      parsed.requestId,
      parsed.error.details,
    );
  }
  if (parsed.data === undefined) {
    throw new ApiError('ERR-UNKNOWN', 'Missing response data', parsed.requestId);
  }
  return { requestId: parsed.requestId, data: parsed.data };
}

export function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export function csrfHeaders(): Record<string, string> {
  const token = getCsrfToken();
  return token ? { 'X-CSRF-Token': token } : {};
}

/**
 * MOD-002 rejects a cookie-borne refresh unless this header is present. A
 * cross-site form cannot set a custom header, so presence is the proof of
 * same-origin; the server does not compare the value.
 */
export const REFRESH_CSRF_HEADER = 'X-Refresh-CSRF';

export function refreshCsrfHeaders(): Record<string, string> {
  return { [REFRESH_CSRF_HEADER]: getCsrfToken() ?? 'same-origin' };
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Attach CSRF header to mutating fetch init (ADR-009 / MOD-002). */
export function withCsrfInit(init: RequestInit = {}): RequestInit {
  const method = (init.method ?? 'GET').toUpperCase();
  if (!MUTATING_METHODS.has(method)) return init;
  return {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      ...csrfHeaders(),
    },
  };
}
